import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AppsIcon from '@atlaskit/icon/core/apps';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import GlobeIcon from '@atlaskit/icon/core/globe';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import { SkeletonItem } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type AgentKnowledgeConfiguration } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	useConnectors,
	useConnectorsIsFetching,
	useEnabledKnowledgeSources,
	useNewlyEnabledConnectors,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';
import { useSideNavModal } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider';

import { RovoLogoForAppSource } from '../../../../common/ui/RovoLogoForAppSource';
import { useProductSortOrder } from '../../../../common/ui/useProductSortOrder';
import { is3pApp } from '../../../../common/utils/is-3p-app';
import type { RovoApp } from '../../../../controllers/chat-mode-store/types';

import { messages } from './messages';
import { OAuthPopupWrapper } from './oauth-popup-wrapper';
import { getKnowledgeSourceDisplayName } from './utils/get-knowledge-source-display-name';

type KnowledgeSourceResponse =
	| { type: 'available'; sources: AgentKnowledgeConfiguration[] }
	| { type: 'errored'; error: Error };

const useAppTypesLoader = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams: { assistanceService } }) => {
		const loadKnowledgeSources = useCallback(async () => {
			try {
				const response = await assistanceService.getAgentKnowledgeConfiguration();
				return { type: 'available', sources: response.knowledgeSources } as KnowledgeSourceResponse;
			} catch (error) {
				return { type: 'errored', error: error as Error } as KnowledgeSourceResponse;
			}
		}, [assistanceService]);

		const { data, refetch, isFetching } = useQuery({
			queryKey: ['agent-knowledge-configuration'],
			queryFn: loadKnowledgeSources,
		});

		return {
			cachedData: data,
			loadKnowledgeSources: refetch,
			isFetching,
		};
	},
);

const styles = cssMap({
	appList: {
		width: '100%',
		paddingLeft: 0,
		marginLeft: 0,
	},
	appListItem: {
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	/* When used inside modal row, let parent provide hover to avoid shade mismatch */
	appListItemHoverFromParent: {
		'&:hover': {
			// @ts-expect-error - cssMap hovered background token variant not in union type; required for interactive state
			backgroundColor: token('color.background.neutral.subtle'),
		},
	},
	appSelector: {
		position: 'relative',
		minWidth: '300px',
		width: '100%',
		height: '380px',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
	},
	appSelectorModal: {
		position: 'relative',
		minWidth: '300px',
		width: '100%',
		height: '100%',
		minHeight: '0px',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	stickyHeader: {
		backgroundColor: token('elevation.surface.overlay'),
		paddingBottom: token('space.100'),
		flexShrink: 0,
	},
	scrollableContent: {
		overflowY: 'auto',
		overflowX: 'hidden',
		scrollbarWidth: 'thin',
		flex: 1,
		minHeight: '0px',
	},
	scrollableContentLegacy: {
		overflowY: 'auto',
		overflowX: 'hidden',
		scrollbarWidth: 'thin',
		flex: 1,
		minHeight: '0px',
		marginTop: token('space.150'),
	},
	clearAll: { marginLeft: 'auto' },
	appSelectorTitle: { width: '100%' },
	rovoLogoWrapper: {
		height: '24px',
		width: '24px',
		borderRadius: token('radius.small'),
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'hidden',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	placeholderLogo: {
		height: '24px',
		width: '24px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.accent.blue.subtle'),
	},
	skeletonItem: {
		paddingInline: token('space.0'),
		minHeight: '32px',
	},
	appItemInline: {
		display: 'flex',
	},
	connectButtonText: {
		color: token('color.text.accent.blue'),
		cursor: 'pointer',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:focus-visible': {
			textDecoration: 'underline',
		},
	},
	appItemContent: {
		flex: 1,
		paddingRight: token('space.100'),
	},
	appItemContentNew: {
		flex: 1,
	},
	sectionHeader: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
	},
	searchInputWrapper: {
		flexShrink: 0,
	},
	modalSectionHeader: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
	},
	modalSectionHeaderWithSearch: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.200'),
		paddingBlock: token('space.075'),
		paddingInlineStart: 0,
	},
	modalSectionList: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
	},
	/** Grouped app lists in filter popup: no outer border (modal uses modalSectionList) */
	popupSectionList: {
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
	},
	modalSectionListItem: {
		marginBlockStart: 0,
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
	},
	/* Row hover only for modal content with new UI - not legacy, not popup */
	modalSectionListItemRowHover: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	modalSectionListItemDivider: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	sourceRow: {
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: 'transparent',
	},
	separator: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlockEnd: token('space.100'),
		marginBlockEnd: token('space.100'),
	},
	iconWrapper: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	viewMoreFooter: {
		flexShrink: 0,
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		marginBlockStart: token('space.100'),
		paddingBlockStart: token('space.100'),
	},
	connectMoreAppsButton: {
		borderRadius: token('radius.small'),
	},
});

export type LoadedRovoApp = {
	friendlyName: string;
	source: string;
	iconUrl: string;
	isSelected: boolean;
	isUserOAuthed?: boolean;
	outboundAuthUrl?: string;
	isNewlyEnabled?: boolean;
};

type AppSelectorState =
	| { type: 'loading' }
	| {
			type: 'available';
			searchTerm: string | undefined;
			apps: LoadedRovoApp[];
	  }
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	| { type: 'errored'; error: any };

interface AppListItemProps {
	app: LoadedRovoApp;
	onAppSelectionChange: (app: LoadedRovoApp, checked: boolean, successfulOauth?: boolean) => void;
	onAppConnectClick?: (source: string) => void;
	/** Passed to OAuth / analytics to identify where Connect was triggered */
	oauthSource: string;
	/** Which product surface opened the flow (e.g. 'chat', 'search') for analytics attribution */
	oauthSurface?: string;
	/** When true, inner button does not apply hover - parent row provides it for uniform shade */
	suppressItemHover?: boolean;
}

const AppListItemLegacy = ({
	app,
	onAppSelectionChange,
	onAppConnectClick,
	oauthSource,
	oauthSurface,
	suppressItemHover,
}: AppListItemProps) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const handleRowClick = () => {
		const newCheckedState = !app.isSelected;
		if (app.isUserOAuthed) {
			onAppSelectionChange(app, newCheckedState);
		}

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'row',
			actionSubjectId: 'rovoChatFilterByAppsClick',
			attributes: {
				isConnected: app.isUserOAuthed,
				interactionElement: 'row',
				isSelected: newCheckedState,
				appSource: app.source,
				appFriendlyName: app.friendlyName,
				is3pApp: is3pApp(app.friendlyName),
			},
		});
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		onAppSelectionChange(app, e.target.checked);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'row',
			actionSubjectId: 'rovoChatFilterByAppsClick',
			attributes: {
				isConnected: app.isUserOAuthed,
				interactionElement: 'checkbox',
				isSelected: e.target.checked,
				appSource: app.source,
				appFriendlyName: app.friendlyName,
				is3pApp: is3pApp(app.friendlyName),
			},
		});
	};

	const appItemContent = (
		<Pressable
			onClick={handleRowClick}
			xcss={cx(
				styles.appListItem,
				suppressItemHover ? styles.appListItemHoverFromParent : undefined,
			)}
			aria-label={
				!app.isUserOAuthed
					? formatMessage(messages.connectApp, { appName: app.friendlyName })
					: app.isSelected
						? formatMessage(messages.deselectApp, { appName: app.friendlyName })
						: formatMessage(messages.selectApp, { appName: app.friendlyName })
			}
		>
			<Inline space="space.100" key={app.source} as="li" xcss={styles.appItemInline}>
				<Checkbox
					id={app.source}
					isChecked={app.isSelected}
					value="default checkbox"
					isDisabled={!app.isUserOAuthed}
					onChange={handleCheckboxChange}
					onClick={(e) => e.stopPropagation()}
					name="checkbox-default"
				/>
				{fg('rovo_chat_use_be_knowledge_icons') ? (
					<Image
						id={app.source}
						width={24}
						height={24}
						src={
							fg('use_new_knowledge_source_icon_url_prefix')
								? app.iconUrl
								: `/gateway/api${app.iconUrl}`
						}
						alt=""
					/>
				) : (
					<Box xcss={styles.rovoLogoWrapper}>
						<RovoLogoForAppSource
							appSource={app.source}
							rovoLogoProps={{ size: 'small', label: '' }}
							fallback={<Box xcss={styles.placeholderLogo} />}
						/>
					</Box>
				)}
				<Box xcss={styles.appItemContent}>
					<Inline grow="fill" spread="space-between" alignBlock="center">
						<Text size="medium" weight="medium" maxLines={1}>
							{app.friendlyName}
						</Text>
						{!app.isUserOAuthed && (
							<Box xcss={styles.connectButtonText} aria-hidden="true">
								<Text size="medium" weight="medium" color="color.link">
									{formatMessage(messages.connect)}
								</Text>
							</Box>
						)}
					</Inline>
				</Box>
			</Inline>
		</Pressable>
	);
	return (
		<OAuthPopupWrapper
			app={app}
			source={oauthSource}
			surface={oauthSurface}
			onAuthorizeAction={() => onAppConnectClick?.(app.source)}
			onCompletedAction={() => {
				onAppSelectionChange(app, true, true);
			}}
			placement="left"
			trigger={(triggerProps) => <div {...triggerProps}>{appItemContent}</div>}
		/>
	);
};

const AppListItem = ({
	app,
	onAppSelectionChange,
	onAppConnectClick,
	oauthSource,
	oauthSurface,
	suppressItemHover,
}: AppListItemProps) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const handleRowClick = () => {
		const newCheckedState = !app.isSelected;
		if (app.isUserOAuthed) {
			onAppSelectionChange(app, newCheckedState);
		}
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'row',
			actionSubjectId: 'rovoChatFilterByAppsClick',
			attributes: {
				isConnected: app.isUserOAuthed,
				interactionElement: 'row',
				isSelected: newCheckedState,
				appSource: app.source,
				appFriendlyName: app.friendlyName,
				is3pApp: is3pApp(app.friendlyName),
			},
		});
	};

	const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		e.nativeEvent.stopImmediatePropagation();
		onAppSelectionChange(app, e.target.checked);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'row',
			actionSubjectId: 'rovoChatFilterByAppsClick',
			attributes: {
				isConnected: app.isUserOAuthed,
				interactionElement: 'toggle',
				isSelected: e.target.checked,
				appSource: app.source,
				appFriendlyName: app.friendlyName,
				is3pApp: is3pApp(app.friendlyName),
			},
		});
	};

	const appItemContent = (
		<Pressable
			onClick={handleRowClick}
			xcss={cx(
				styles.appListItem,
				suppressItemHover ? styles.appListItemHoverFromParent : undefined,
			)}
			aria-label={
				!app.isUserOAuthed
					? formatMessage(messages.connectApp, { appName: app.friendlyName })
					: app.isSelected
						? formatMessage(messages.deselectApp, { appName: app.friendlyName })
						: formatMessage(messages.selectApp, { appName: app.friendlyName })
			}
		>
			<Inline space="space.100" key={app.source} as="li" xcss={styles.appItemInline}>
				{fg('rovo_chat_use_be_knowledge_icons') ? (
					<Image
						id={app.source}
						width={24}
						height={24}
						src={
							fg('use_new_knowledge_source_icon_url_prefix')
								? app.iconUrl
								: `/gateway/api${app.iconUrl}`
						}
						alt=""
					/>
				) : (
					<Box xcss={styles.rovoLogoWrapper}>
						<RovoLogoForAppSource
							appSource={app.source}
							rovoLogoProps={{ size: 'small', label: '' }}
							fallback={<Box xcss={styles.placeholderLogo} />}
						/>
					</Box>
				)}
				<Box xcss={styles.appItemContentNew}>
					<Inline grow="fill" spread="space-between" alignBlock="center">
						<Inline space="space.100" alignBlock="center">
							<Text size="medium" weight="medium" maxLines={1}>
								{app.friendlyName}
							</Text>
							{app.isNewlyEnabled && (
								<Lozenge appearance="discovery" isBold>
									{formatMessage(messages.newBadge)}
								</Lozenge>
							)}
						</Inline>
						{app.isUserOAuthed ? (
							<Toggle
								id={app.source}
								isChecked={app.isSelected}
								onChange={handleToggleChange}
								size="regular"
							/>
						) : (
							<Box xcss={styles.connectButtonText} aria-hidden="true">
								<Text size="medium" weight="medium" color="color.link">
									{formatMessage(messages.connect)}
								</Text>
							</Box>
						)}
					</Inline>
				</Box>
			</Inline>
		</Pressable>
	);
	return (
		<OAuthPopupWrapper
			app={app}
			source={oauthSource}
			surface={oauthSurface}
			onAuthorizeAction={() => onAppConnectClick?.(app.source)}
			onCompletedAction={() => {
				onAppSelectionChange(app, true, true);
			}}
			placement="left"
			trigger={(triggerProps) => <div {...triggerProps}>{appItemContent}</div>}
		/>
	);
};

const AppsList = ({
	apps,
	isLoading,
	onAppSelectionChange,
	onAppConnectClick,
	useLegacyItem,
	oauthSource,
	oauthSurface,
	showItemDividers = false,
}: {
	apps: LoadedRovoApp[];
	isLoading: boolean;
	onAppSelectionChange: (app: LoadedRovoApp, checked: boolean, successfulOauth?: boolean) => void;
	onAppConnectClick?: (source: string) => void;
	useLegacyItem?: boolean;
	oauthSource: string;
	oauthSurface?: string;
	showItemDividers?: boolean;
}) => {
	if (isLoading) {
		return (
			<Stack as="ul" space="space.100" xcss={styles.appList}>
				{[...Array(7)].map((_, index) => (
					<Box key={index} xcss={styles.skeletonItem}>
						<SkeletonItem hasIcon={true} />
					</Box>
				))}
			</Stack>
		);
	}

	const ItemComponent = useLegacyItem ? AppListItemLegacy : AppListItem;

	return (
		<Stack as="ul" space="space.0" xcss={styles.appList}>
			{apps.map((app, index) => {
				const isLast = index === apps.length - 1;
				const item = (
					<ItemComponent
						key={app.source}
						app={app}
						oauthSource={oauthSource}
						oauthSurface={oauthSurface}
						onAppConnectClick={onAppConnectClick}
						onAppSelectionChange={onAppSelectionChange}
						suppressItemHover={showItemDividers && !useLegacyItem}
					/>
				);
				if (showItemDividers) {
					return (
						<Box
							key={app.source}
							as="li"
							xcss={cx(
								styles.modalSectionListItem,
								!isLast && styles.modalSectionListItemDivider,
								!useLegacyItem && styles.modalSectionListItemRowHover,
							)}
						>
							{item}
						</Box>
					);
				}
				return item;
			})}
		</Stack>
	);
};

export const FilterByAppsSelector = ({
	assistanceServiceParams,
	preservedSelectedApps,
	onChange,
	onClose,
	hideBackButton = false,
	hideViewMoreButton = false,
	showGroupedSectionsWithCounts = false,
	maxVisibleItems,
	webSearchEnabled,
	onWebSearchToggle,
	orgKnowledgeEnabled,
	onOrgKnowledgeToggle,
	oauthSource = 'appFilterMenu',
	oauthSurface,
	onAppConnectClick,
	initialScrollToAvailableApps = false,
	maxUnconnectedApps,
}: {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	preservedSelectedApps: RovoApp[];
	onChange: (apps: RovoApp[]) => void;
	onClose?: () => void;
	hideBackButton?: boolean;
	hideViewMoreButton?: boolean;
	showGroupedSectionsWithCounts?: boolean;
	/** Max number of connectors to show in the list when M1 experiment has isEnabled and showRovoSettingsModal (e.g. 7). Undefined = show all. */
	maxVisibleItems?: number;
	webSearchEnabled?: boolean;
	onWebSearchToggle?: (enabled: boolean) => void;
	orgKnowledgeEnabled?: boolean;
	onOrgKnowledgeToggle?: (enabled: boolean) => void;
	/** Passed to OAuth when user connects an app; use `rovoSettingsModal` when shown inside the side nav Rovo settings modal */
	oauthSource?: string;
	/** Which product surface opened the flow (e.g. 'chat', 'search') for analytics attribution */
	oauthSurface?: string;
	onAppConnectClick?: (source: string) => void;
	/** When true (e.g. Rovo settings modal opened with scrollToAvailableApps), scroll to the available-apps section once content is ready */
	initialScrollToAvailableApps?: boolean;
	/** Max number of unconnected (available-to-connect) apps to show, driven by
	 *  rovo_chat_settings_modal_new_apps_tab_enabled experiment cohort (5 for short-list, 10 for long-list).
	 *  Undefined = show all (control). When defined, test cohort UI is applied
	 *  (shows Discover more apps footer, opens Discovery tab). */
	maxUnconnectedApps?: number;
}): React.JSX.Element => {
	const isChatAppsTabTestCohort = maxUnconnectedApps !== undefined;
	useEnsureConnectorsLoaded({ assistanceServiceParams });
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [appSelectorState, setAppSelectorState] = useState<AppSelectorState>({ type: 'loading' });
	const [allSources] = useEnabledKnowledgeSources();
	const [storeFetching] = useConnectorsIsFetching();
	const newlyEnabledConnectors = useNewlyEnabledConnectors();
	const [connectors] = useConnectors();
	const { loadKnowledgeSources, cachedData } = useAppTypesLoader({ assistanceServiceParams });
	const inputRef = useRef<HTMLInputElement>(null);
	const appsAvailableToConnectSectionRef = useRef<HTMLDivElement>(null);
	const hasInitializedRef = useRef(false);
	const newlyEnabledConnectorSet = useMemo(
		() => new Set(newlyEnabledConnectors.map((c) => c.source)),
		[newlyEnabledConnectors],
	);
	const isOnFiltersChangeEnabled = fg('rovo-chat-input-on-filters-change-enabled');

	const { open: openSideNavModal, disabled: sideNavModalDisabled } = useSideNavModal();
	const productSortOrderProvider = useProductSortOrder();

	const scrollToAppsAvailableToConnect = useCallback(() => {
		appsAvailableToConnectSectionRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}, []);

	const handleConnectMoreClick = useCallback(() => {
		/** Already inside Rovo settings modal — opening again is a no-op; scroll in place */
		if (oauthSource === 'rovoSettingsModal') {
			scrollToAppsAvailableToConnect();
			return;
		}
		if (
			!sideNavModalDisabled &&
			(FeatureGates.getExperimentValue(
				'rovo_chat_3p_app_discovery_m1_experiment',
				'showRovoSettingsModal',
				false,
			) ||
				isChatAppsTabTestCohort)
		) {
			openSideNavModal({
				title: formatMessage(messages.viewMoreModalTitle),
				searchPlaceholder: formatMessage(messages.searchSettingsPlaceholder),
				navAriaLabel: formatMessage(messages.viewMoreModalTitle),
				// In a test cohort, "Connect more" bookends the list with the
				// footer's "View more" — both should open the new Discovery tab.
				defaultSelectedId: isChatAppsTabTestCohort ? 'discovery' : 'connected-apps',
				scrollToAvailableApps: true,
			});
		} else {
			scrollToAppsAvailableToConnect();
		}
	}, [
		oauthSource,
		openSideNavModal,
		sideNavModalDisabled,
		formatMessage,
		scrollToAppsAvailableToConnect,
		isChatAppsTabTestCohort,
	]);

	useEffect(() => {
		if (
			FeatureGates.getExperimentValue(
				'rovo_chat_3p_app_discovery_m1_experiment',
				'isEnabled',
				false,
			)
		) {
			if (storeFetching) {
				return;
			}
			if (allSources.length === 0 && appSelectorState.type === 'loading') {
				return;
			}
			if (hasInitializedRef.current) {
				setAppSelectorState((currentState) => {
					if (currentState.type !== 'available') {
						return currentState;
					}
					const selectedSet = new Set(preservedSelectedApps.map((a) => a.source));
					const updatedApps = allSources.map((source) => ({
						friendlyName: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
						source: source.source,
						iconUrl: source.iconUrl,
						isSelected: selectedSet.has(source.source),
						outboundAuthUrl: source.outboundAuthUrl,
						isUserOAuthed: source.isUserOAuthed,
						isNewlyEnabled: newlyEnabledConnectorSet.has(source.source),
					}));
					return { ...currentState, apps: updatedApps };
				});
				return;
			}
			const loadApps = async () => {
				if (hasInitializedRef.current) {
					return;
				}
				const sortProduct = await productSortOrderProvider();
				const shouldSelectAll = preservedSelectedApps.length === 0 && orgKnowledgeEnabled !== false;

				const apps = allSources
					.map((source) => ({
						friendlyName: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
						source: source.source,
						iconUrl: source.iconUrl,
						isSelected: shouldSelectAll
							? !!source.isUserOAuthed
							: preservedSelectedApps.find((app) => app.source === source.source) !== undefined,
						outboundAuthUrl: source.outboundAuthUrl,
						isUserOAuthed: source.isUserOAuthed,
						isNewlyEnabled: newlyEnabledConnectorSet.has(source.source),
					}))
					.sort((a, b) => sortProduct(a.source, b.source));

				setAppSelectorState({
					type: 'available',
					apps,
					searchTerm: undefined,
				});

				if (shouldSelectAll) {
					const selectedApps = apps.filter((app) => app.isSelected);
					onChange(selectedApps);
				}
				hasInitializedRef.current = true;
			};
			loadApps();
		} else {
			if (appSelectorState.type !== 'loading') {
				return;
			}
			const loadApps = async () => {
				let response: KnowledgeSourceResponse;
				if (cachedData && cachedData.type === 'available') {
					response = cachedData;
				} else {
					const responseOrNull = await loadKnowledgeSources();
					if (!responseOrNull) {
						return;
					}
					response = responseOrNull;
				}
				if (response.type === 'available') {
					const sortProduct = await productSortOrderProvider();
					setAppSelectorState({
						type: 'available',
						apps:
							response.sources
								?.map((source) => ({
									friendlyName: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
									source: source.source,
									iconUrl: source.iconUrl,
									isSelected:
										preservedSelectedApps.find((app) => app.source === source.source) !== undefined,
									outboundAuthUrl: source.outboundAuthUrl,
									isUserOAuthed: source.isUserOAuthed,
								}))
								?.sort((a, b) => sortProduct(a.source, b.source)) ?? [],
						searchTerm: undefined,
					});
				} else {
					setAppSelectorState({ type: 'errored', error: response.error });
				}
			};
			loadApps();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		productSortOrderProvider,
		allSources,
		storeFetching,
		preservedSelectedApps,
		appSelectorState.type,
		cachedData,
		loadKnowledgeSources,
		onChange,
		newlyEnabledConnectorSet,
	]);

	useEffect(() => {
		if (
			FeatureGates.getExperimentValue(
				'rovo_chat_3p_app_discovery_m1_experiment',
				'isEnabled',
				false,
			) &&
			!hasInitializedRef.current
		) {
			return;
		}
		setAppSelectorState((currentState) => {
			if (currentState.type === 'loading' || currentState.type === 'errored') {
				return currentState;
			}
			return {
				...currentState,
				apps: currentState.apps.map((app) => ({
					...app,
					isSelected: preservedSelectedApps.find((a) => a.source === app.source) !== undefined,
				})),
			};
		});
	}, [preservedSelectedApps, setAppSelectorState]);

	useEffect(() => {
		if (
			!FeatureGates.getExperimentValue(
				'rovo_chat_3p_app_discovery_m1_experiment',
				'isEnabled',
				false,
			) &&
			appSelectorState.type === 'available'
		) {
			inputRef.current?.focus();
		}
	}, [appSelectorState]);

	const onAppSelectionChange = useCallback(
		(app: LoadedRovoApp, checked: boolean, successfulOauth?: boolean) => {
			let filteredApps: RovoApp[] = preservedSelectedApps.filter((a) => a.source !== app.source);
			let updatedApp = { ...app };

			if (successfulOauth && appSelectorState.type === 'available') {
				updatedApp = { ...app, isUserOAuthed: true };

				setAppSelectorState((currentState) => {
					if (currentState.type === 'loading' || currentState.type === 'errored') {
						return currentState;
					}
					return {
						...currentState,
						apps: currentState.apps.map((existingApp) =>
							existingApp.source === app.source
								? { ...existingApp, isUserOAuthed: true, isSelected: true }
								: existingApp,
						),
					};
				});
			}

			if (checked) {
				filteredApps = [...filteredApps, updatedApp];
			}
			onChange(filteredApps);
		},
		[preservedSelectedApps, onChange, setAppSelectorState, appSelectorState.type],
	);

	const handleSearchChange = useCallback(
		(searchText: string) => {
			setAppSelectorState((currentState) => {
				if (currentState.type === 'loading' || currentState.type === 'errored') {
					return currentState;
				}
				return {
					...currentState,
					searchTerm: searchText,
				};
			});

			sendAnalyticsEvent({
				action: 'searched',
				actionSubject: 'input',
				actionSubjectId: 'rovoChatFilterByAppsSearchInput',
				attributes: {
					searchValueLength: searchText.length,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const sortedAndFilteredApps = useMemo(() => {
		if (appSelectorState.type !== 'available') {
			return [];
		}

		if (
			FeatureGates.getExperimentValue(
				'rovo_chat_3p_app_discovery_m1_experiment',
				'isEnabled',
				false,
			)
		) {
			return [...appSelectorState.apps].sort((a, b) => {
				const aConnected = a.isUserOAuthed ? 1 : 0;
				const bConnected = b.isUserOAuthed ? 1 : 0;
				if (aConnected !== bConnected) {
					return bConnected - aConnected;
				}
				const aTimestamp = connectors[a.source]?.connectedAt
					? new Date(connectors[a.source].connectedAt!).getTime()
					: 0;
				const bTimestamp = connectors[b.source]?.connectedAt
					? new Date(connectors[b.source].connectedAt!).getTime()
					: 0;
				return bTimestamp - aTimestamp;
			});
		}

		let filtered = appSelectorState.apps.filter((app) => {
			if (!appSelectorState.searchTerm) {
				return true;
			}
			return app.friendlyName.toLowerCase().includes(appSelectorState.searchTerm.toLowerCase());
		});

		return filtered.sort((a, b) => {
			const aConnected = a.isUserOAuthed ? 1 : 0;
			const bConnected = b.isUserOAuthed ? 1 : 0;
			return bConnected - aConnected;
		});
	}, [appSelectorState, connectors]);

	const searchTerm =
		appSelectorState.type === 'available' ? appSelectorState.searchTerm : undefined;
	const { connectedApps, availableToConnectApps } = useMemo(() => {
		if (appSelectorState.type !== 'available') {
			return {
				connectedApps: [] as LoadedRovoApp[],
				availableToConnectApps: [] as LoadedRovoApp[],
			};
		}
		let apps = sortedAndFilteredApps;
		if (showGroupedSectionsWithCounts && searchTerm) {
			apps = apps.filter((app) =>
				app.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}
		const connected = apps.filter((app) => app.isUserOAuthed);
		const available = apps.filter((app) => !app.isUserOAuthed);
		return { connectedApps: connected, availableToConnectApps: available };
	}, [appSelectorState.type, searchTerm, sortedAndFilteredApps, showGroupedSectionsWithCounts]);

	const totalAppsInSections =
		showGroupedSectionsWithCounts && appSelectorState.type === 'available'
			? connectedApps.length + availableToConnectApps.length
			: 0;

	const shouldLimitPopupGroupedApps =
		showGroupedSectionsWithCounts &&
		oauthSource !== 'rovoSettingsModal' &&
		FeatureGates.getExperimentValue(
			'rovo_chat_3p_app_discovery_m1_experiment',
			'showRovoSettingsModal',
			false,
		);

	const { connectedAppsForDisplay, availableToConnectAppsForDisplay } = useMemo(() => {
		if (shouldLimitPopupGroupedApps) {
			// M1 limiter: cap total visible items (connected + unconnected) to 7
			const maxVisible = 7;
			const connectedSlice = connectedApps.slice(0, Math.min(connectedApps.length, maxVisible));
			const remaining = maxVisible - connectedSlice.length;
			const availableSlice = availableToConnectApps.slice(0, remaining);
			return {
				connectedAppsForDisplay: connectedSlice,
				availableToConnectAppsForDisplay: availableSlice,
			};
		} else if (maxUnconnectedApps !== undefined) {
			// Cohort-based limit: show all connected apps, cap unconnected apps only
			return {
				connectedAppsForDisplay: connectedApps,
				availableToConnectAppsForDisplay: availableToConnectApps.slice(0, maxUnconnectedApps),
			};
		}
		return {
			connectedAppsForDisplay: connectedApps,
			availableToConnectAppsForDisplay: availableToConnectApps,
		};
	}, [shouldLimitPopupGroupedApps, connectedApps, availableToConnectApps, maxUnconnectedApps]);

	useEffect(() => {
		if (!initialScrollToAvailableApps) {
			return;
		}
		if (appSelectorState.type !== 'available' || availableToConnectApps.length === 0) {
			return;
		}
		const frame = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				appsAvailableToConnectSectionRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			});
		});
		return () => cancelAnimationFrame(frame);
	}, [initialScrollToAvailableApps, appSelectorState.type, availableToConnectApps.length]);

	const renderContent = () => {
		switch (appSelectorState.type) {
			case 'errored':
				return <></>;
			case 'loading':
			case 'available':
				if (showGroupedSectionsWithCounts && appSelectorState.type === 'available') {
					const isModalGroupedLayout = oauthSource === 'rovoSettingsModal';

					const connectedAppsSummaryRow = (
						<Inline space="space.100" alignBlock="center">
							<Text
								size="small"
								weight={isModalGroupedLayout ? 'bold' : 'semibold'}
								color={isModalGroupedLayout ? 'color.text' : 'color.text.subtlest'}
							>
								{formatMessage(messages.connectedAppsOutOfTotal, {
									connectedCount: connectedApps.length,
									totalCount: totalAppsInSections,
								})}
							</Text>
							{availableToConnectApps.length > 0 && (
								<Pressable
									role="button"
									onClick={handleConnectMoreClick}
									xcss={styles.connectMoreAppsButton}
								>
									<Text size="small" weight="medium" color="color.link">
										{formatMessage(messages.connectMoreApps)}
									</Text>
								</Pressable>
							)}
						</Inline>
					);
					return (
						<Stack space="space.200">
							<Box>
								{isModalGroupedLayout ? (
									<Box xcss={styles.modalSectionHeaderWithSearch}>
										{connectedAppsSummaryRow}
										<Box xcss={styles.searchInputWrapper}>
											<SearchInput
												placeholderText={formatMessage(messages.searchAppsPlaceholder)}
												onSearch={handleSearchChange}
												isDisabled={false}
												debounceTime={200}
												isCompact
											/>
										</Box>
									</Box>
								) : (
									<Box xcss={styles.sectionHeader}>{connectedAppsSummaryRow}</Box>
								)}
								{connectedAppsForDisplay.length > 0 && (
									<Box
										xcss={isModalGroupedLayout ? styles.modalSectionList : styles.popupSectionList}
									>
										<AppsList
											apps={connectedAppsForDisplay}
											onAppSelectionChange={onAppSelectionChange}
											isLoading={false}
											useLegacyItem={
												!FeatureGates.getExperimentValue(
													'rovo_chat_3p_app_discovery_m1_experiment',
													'isEnabled',
													false,
												)
											}
											oauthSource={oauthSource}
											oauthSurface={oauthSurface}
											onAppConnectClick={onAppConnectClick}
											showItemDividers={oauthSource === 'rovoSettingsModal'}
										/>
									</Box>
								)}
							</Box>
							{availableToConnectAppsForDisplay.length > 0 && (
								<Box ref={appsAvailableToConnectSectionRef}>
									<Box
										xcss={isModalGroupedLayout ? styles.modalSectionHeader : styles.sectionHeader}
									>
										<Text
											size="small"
											weight={isModalGroupedLayout ? 'bold' : 'semibold'}
											color={isModalGroupedLayout ? 'color.text' : 'color.text.subtlest'}
										>
											{formatMessage(messages.appsAvailableToConnectSectionHeader, {
												count: availableToConnectApps.length,
											})}
										</Text>
									</Box>
									<Box
										xcss={isModalGroupedLayout ? styles.modalSectionList : styles.popupSectionList}
									>
										<AppsList
											apps={availableToConnectAppsForDisplay}
											onAppSelectionChange={onAppSelectionChange}
											isLoading={false}
											useLegacyItem={
												!FeatureGates.getExperimentValue(
													'rovo_chat_3p_app_discovery_m1_experiment',
													'isEnabled',
													false,
												)
											}
											oauthSource={oauthSource}
											oauthSurface={oauthSurface}
											onAppConnectClick={onAppConnectClick}
											showItemDividers={oauthSource === 'rovoSettingsModal'}
										/>
									</Box>
								</Box>
							)}
						</Stack>
					);
				}
				const appsToShow =
					maxVisibleItems !== undefined
						? sortedAndFilteredApps.slice(0, maxVisibleItems)
						: sortedAndFilteredApps;
				return (
					<AppsList
						apps={appsToShow}
						onAppSelectionChange={onAppSelectionChange}
						isLoading={appSelectorState.type === 'loading'}
						useLegacyItem={
							!FeatureGates.getExperimentValue(
								'rovo_chat_3p_app_discovery_m1_experiment',
								'isEnabled',
								false,
							)
						}
						oauthSource={oauthSource}
						oauthSurface={oauthSurface}
						onAppConnectClick={onAppConnectClick}
					/>
				);
		}
	};

	if (
		FeatureGates.getExperimentValue('rovo_chat_3p_app_discovery_m1_experiment', 'isEnabled', false)
	) {
		const showSourcesSection =
			onWebSearchToggle !== undefined || onOrgKnowledgeToggle !== undefined;

		return (
			<>
				<Box
					xcss={
						showGroupedSectionsWithCounts && oauthSource === 'rovoSettingsModal'
							? styles.appSelectorModal
							: styles.appSelector
					}
				>
					<Box xcss={styles.scrollableContent}>
						{showSourcesSection && (
							<Box xcss={styles.separator}>
								<Box xcss={styles.sectionHeader}>
									<Text size="small" weight="semibold" color="color.text.subtlest">
										{formatMessage(messages.sourcesHeader)}
									</Text>
								</Box>
								<Stack space="space.0">
									{onWebSearchToggle !== undefined && (
										<Pressable
											onClick={(e) => {
												// Only handle clicks on the row itself, not on the Toggle (which handles its own events)
												// This was causing a double state change on the toggle.
												if (isOnFiltersChangeEnabled && e.target instanceof HTMLInputElement) {
													return;
												}
												onWebSearchToggle(!webSearchEnabled);
											}}
											xcss={styles.sourceRow}
										>
											<Inline space="space.100" alignBlock="center">
												<Box xcss={styles.iconWrapper}>
													<GlobeIcon label="" size="medium" />
												</Box>
												<Box xcss={styles.appItemContentNew}>
													<Inline grow="fill" spread="space-between" alignBlock="center">
														<Text size="medium" weight="medium">
															{formatMessage(messages.includeWebResults)}
														</Text>
														<Toggle
															id="web_search_toggle"
															isChecked={!!webSearchEnabled}
															onChange={(e) => {
																e.stopPropagation();
																onWebSearchToggle(e.target.checked);
															}}
															size="regular"
														/>
													</Inline>
												</Box>
											</Inline>
										</Pressable>
									)}
									{onOrgKnowledgeToggle !== undefined && (
										<Pressable
											onClick={(e) => {
												// Only handle clicks on the row itself, not on the Toggle (which handles its own events)
												// This was causing a double state change on the toggle.
												if (isOnFiltersChangeEnabled && e.target instanceof HTMLInputElement) {
													return;
												}
												onOrgKnowledgeToggle(!orgKnowledgeEnabled);
											}}
											xcss={styles.sourceRow}
										>
											<Inline space="space.100" alignBlock="center">
												<Box xcss={styles.iconWrapper}>
													<OfficeBuildingIcon label="" size="medium" />
												</Box>
												<Box xcss={styles.appItemContentNew}>
													<Inline grow="fill" spread="space-between" alignBlock="center">
														<Text size="medium" weight="medium">
															{formatMessage(messages.searchCompanyKnowledge)}
														</Text>
														<Toggle
															id="org_knowledge_toggle"
															isChecked={!!orgKnowledgeEnabled}
															onChange={(e) => {
																e.stopPropagation();
																onOrgKnowledgeToggle(e.target.checked);
															}}
															size="regular"
														/>
													</Inline>
												</Box>
											</Inline>
										</Pressable>
									)}
								</Stack>
							</Box>
						)}
						{!showGroupedSectionsWithCounts && (
							<Box xcss={styles.sectionHeader}>
								<Text size="small" weight="semibold" color="color.text.subtlest">
									{formatMessage(messages.appsHeader)}
								</Text>
							</Box>
						)}
						{renderContent()}
					</Box>
					{!hideViewMoreButton &&
						!sideNavModalDisabled &&
						(FeatureGates.getExperimentValue(
							'rovo_chat_3p_app_discovery_m1_experiment',
							'showRovoSettingsModal',
							false,
						) ||
							isChatAppsTabTestCohort) && (
							/* Use "Discover more apps" when shown via the new cohort experiment;
							   keep the existing "View More" label for the M1 experiment path */
							<Box xcss={styles.viewMoreFooter}>
								<Pressable
									xcss={styles.sourceRow}
									onClick={() => {
										openSideNavModal({
											title: formatMessage(messages.viewMoreModalTitle),
											searchPlaceholder: formatMessage(messages.searchSettingsPlaceholder),
											navAriaLabel: formatMessage(messages.viewMoreModalTitle),
											defaultSelectedId: isChatAppsTabTestCohort ? 'discovery' : 'connected-apps',
										});
									}}
								>
									<Inline space="space.100" alignBlock="center">
										<Box xcss={styles.iconWrapper}>
											<AppsIcon label="" size="medium" />
										</Box>
										<Text size="medium" weight="medium">
											{formatMessage(
												isChatAppsTabTestCohort ? messages.discoverMoreApps : messages.viewMore,
											)}
										</Text>
									</Inline>
								</Pressable>
							</Box>
						)}
				</Box>
			</>
		);
	}

	return (
		<Box xcss={styles.appSelector}>
			<Box xcss={styles.stickyHeader}>
				<Stack space="space.100">
					<Inline space="space.100" alignBlock="center" xcss={styles.appSelectorTitle}>
						{!hideBackButton && (
							<IconButton
								appearance="subtle"
								spacing="compact"
								icon={() => <ChevronLeftIcon size="medium" label="" />}
								onClick={() => {
									onClose?.();
								}}
								label={formatMessage(messages.backButtonLabel)}
							/>
						)}
						<Text size="medium" weight="semibold" role="heading" aria-level={2}>
							{formatMessage(messages.filterByApps)}
						</Text>
						<Box xcss={styles.clearAll}>
							<Button
								spacing="compact"
								onClick={() => onChange([])}
								isDisabled={appSelectorState.type === 'loading'}
							>
								{formatMessage(messages.clearAll)}
							</Button>
						</Box>
					</Inline>

					<SearchInput
						placeholderText={formatMessage(messages.searchAppsPlaceholder)}
						onSearch={handleSearchChange}
						isDisabled={appSelectorState.type === 'loading'}
						debounceTime={200}
						ref={inputRef}
					/>
				</Stack>
			</Box>

			<Box xcss={styles.scrollableContentLegacy}>{renderContent()}</Box>
		</Box>
	);
};
